import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle, 
  Circle, 
  AlertCircle, 
  ChevronDown, 
  ChevronUp, 
  Search, 
  Brain, 
  Gavel, 
  ClipboardList, 
  FileText,
  Sparkles,
  Bot,
  Network
} from 'lucide-react';
import { getLanguageDirection } from '../../i18n/config';
import { useAppSelector } from '../../redux/store';
import { selectActiveProgressForChat, FeedUpdate } from '../../redux/slices/simpleLegalProgressSlice';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { cn } from '@/lib/utils';

interface SimpleLegalQueryProgressProps {
  chatId: string;
  compact?: boolean;
}

// ✅ Stage definitions with elegant descriptions
const STAGE_DEFINITIONS = [
  { 
    id: 'initialization', 
    name: 'Setup', 
    description: 'Preparing legal research framework',
    icon: ClipboardList, 
    color: 'primary' 
  },
  { 
    id: 'decomposition', 
    name: 'Analysis', 
    description: 'Breaking down your question into research areas',
    icon: Brain, 
    color: 'primary' 
  },
  { 
    id: 'sub_query_processing', 
    name: 'Research', 
    description: 'Conducting comprehensive legal research',
    icon: Search, 
    color: 'primary' 
  },
  { 
    id: 'synthesis', 
    name: 'Synthesis', 
    description: 'Combining findings into coherent analysis',
    icon: FileText, 
    color: 'primary' 
  },
  { 
    id: 'finalization', 
    name: 'Review', 
    description: 'Final review and formatting',
    icon: Gavel, 
    color: 'success' 
  }
];

/**
 * ✅ Enhanced Legal Query Progress Component
 * Uses server-calculated progress with rich UI features
 */
const SimpleLegalQueryProgress: React.FC<SimpleLegalQueryProgressProps> = ({
  chatId,
  compact = false
}) => {
  // ✅ Get simple progress for this chat - MUST be called before early return
  const progress = useAppSelector(state => selectActiveProgressForChat(state, chatId));
  
  const { t, i18n } = useTranslation();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';
  const [expanded, setExpanded] = React.useState(!compact);
  
  // ✅ Feed scroll state management
  const [isAutoScroll, setIsAutoScroll] = React.useState(true);
  const [showScrollButton, setShowScrollButton] = React.useState(false);
  const feedContainerRef = React.useRef<HTMLDivElement>(null);
  const lastFeedLengthRef = React.useRef(0);

  // ✅ Animation queue state management
  const [displayedFeed, setDisplayedFeed] = React.useState<FeedUpdate[]>([]);
  const [animationQueue, setAnimationQueue] = React.useState<FeedUpdate[]>([]);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const displayedFeedIdsRef = React.useRef<Set<string>>(new Set());

  // ✅ Detect new feed items from Redux and add to animation queue
  React.useEffect(() => {
    if (!progress) return; // Guard against null progress
    
    const reduxFeedItems = progress.feedUpdates || [];
    
    // Find new items that haven't been displayed yet
    const newItems = reduxFeedItems.filter(item => !displayedFeedIdsRef.current.has(item.id));
    
    if (newItems.length > 0) {
      console.log(`🎬 [ANIMATION] Detected ${newItems.length} new feed items from Redux`);
      
      if (isInitialLoad && newItems.length > 3) {
        // On initial load: show 3 most recent instantly, queue the rest
        const recentItems = newItems.slice(-3);
        const olderItems = newItems.slice(0, -3);
        
        console.log(`🎬 [ANIMATION] Initial load: showing ${recentItems.length} instantly, queuing ${olderItems.length} for animation`);
        
        // Add recent items directly to displayed feed
        setDisplayedFeed(prev => [...prev, ...recentItems]);
        recentItems.forEach(item => displayedFeedIdsRef.current.add(item.id));
        
        // Queue older items for animation (reversed so oldest animates first)
        setAnimationQueue(prev => [...prev, ...olderItems.reverse()]);
        // ✅ Mark queued items as "seen" to prevent duplicates
        olderItems.forEach(item => displayedFeedIdsRef.current.add(item.id));
        
        setIsInitialLoad(false);
      } else {
        // Normal operation: queue all new items for animation
        console.log(`🎬 [ANIMATION] Queuing ${newItems.length} items for animation`);
        setAnimationQueue(prev => [...prev, ...newItems]);
        // ✅ Mark queued items as "seen" to prevent duplicates
        newItems.forEach(item => displayedFeedIdsRef.current.add(item.id));
      }
    }
  }, [progress?.feedUpdates, isInitialLoad, progress]);

  // ✅ Process animation queue one item at a time
  React.useEffect(() => {
    if (!progress) return; // Guard against null progress
    
    if (animationQueue.length > 0 && !isAnimating) {
      setIsAnimating(true);
      const nextItem = animationQueue[0];
      
      console.log(`🎬 [ANIMATION] Rendering item: ${nextItem.message.substring(0, 60)}...`);
      
      // Add item to displayed feed (ID already in ref from queueing)
      setDisplayedFeed(prev => [...prev, nextItem]);
      
      // Determine delay based on load state
      const delay = isInitialLoad ? 150 : 500; // 150ms for initial load, 500ms for normal
      
      // Remove from queue and continue after delay
      setTimeout(() => {
        setAnimationQueue(prev => prev.slice(1));
        setIsAnimating(false);
      }, delay);
    }
  }, [animationQueue, isAnimating, isInitialLoad, progress]);

  // ✅ Auto-scroll effect when new feed items are displayed
  React.useEffect(() => {
    if (!progress) return; // Guard against null progress
    
    const feedItems = displayedFeed || [];
    
    // Check if new items were added
    if (feedItems.length > lastFeedLengthRef.current && isAutoScroll) {
      // Scroll to bottom
      if (feedContainerRef.current) {
        feedContainerRef.current.scrollTop = feedContainerRef.current.scrollHeight;
      }
    }
    
    lastFeedLengthRef.current = feedItems.length;
  }, [displayedFeed, isAutoScroll, progress]);

  // Don't render if no active progress (check AFTER all hooks have been called)
  if (!progress) {
    return null;
  }

  // ✅ Helper functions for rich UI
  const getStatusColor = (): string => {
    switch (progress.status) {
      case 'completed':
        return 'text-success';
      case 'error':
        return 'text-destructive';
      default:
        return 'text-gold';
    }
  };

  const getStatusBgColor = (): string => {
    switch (progress.status) {
      case 'completed':
        return 'bg-success/5 border-success/30';
      case 'error':
        return 'bg-destructive/5 border-destructive/30';
      default:
        return 'bg-gold/5 border-gold/20';
    }
  };

  const getFeedFadeColor = (): string => {
    switch (progress.status) {
      case 'completed':
        return 'from-success/5 via-success/3 to-transparent';
      case 'error':
        return 'from-destructive/5 via-destructive/3 to-transparent';
      default:
        return 'from-gold/5 via-gold/3 to-transparent';
    }
  };

  // ✅ Check if MQTT is active (recent updates) vs polling fallback
  const isMQTTActive = () => {
    const now = Date.now();
    const timeSinceUpdate = now - progress.lastUpdate;
    return timeSinceUpdate < 45000; // 45 seconds
  };

  const isPollingMode = !isMQTTActive();

  const getCurrentStageIndex = () => {
    return STAGE_DEFINITIONS.findIndex(stage => stage.id === progress.currentStage);
  };

  const getStageIcon = (stageId: string, status: 'pending' | 'active' | 'completed' | 'error') => {
    const stage = STAGE_DEFINITIONS.find(s => s.id === stageId);
    const IconComponent = stage?.icon || ClipboardList;
    
    const iconColor = status === 'error' ? 'text-destructive' :
                     status === 'completed' ? 'text-success' :
                     status === 'active' ? 'text-gold' :
                     'text-muted-foreground';
    
    return (
      <div className="flex items-center mr-2">
        {status === 'completed' ? (
          <CheckCircle className={cn('w-4 h-4', 'text-success')} />
        ) : status === 'error' ? (
          <AlertCircle className={cn('w-4 h-4', 'text-destructive')} />
        ) : status === 'active' ? (
          <IconComponent className={cn('w-4 h-4', iconColor)} />
        ) : (
          <Circle className={cn('w-4 h-4', iconColor)} />
        )}
      </div>
    );
  };

  const getStageStatus = (stageIndex: number) => {
    const currentIndex = getCurrentStageIndex();
    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) {
      return progress.status === 'error' ? 'error' : 'active';
    }
    return 'pending';
  };

  // ✅ Handle scroll event to detect manual scrolling
  const handleFeedScroll = () => {
    if (!feedContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = feedContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // Within 50px of bottom
    
    setIsAutoScroll(isAtBottom);
    setShowScrollButton(!isAtBottom);
  };

  // ✅ Handle scroll-to-bottom button click
  const handleScrollToBottom = () => {
    if (feedContainerRef.current) {
      feedContainerRef.current.scrollTop = feedContainerRef.current.scrollHeight;
      setIsAutoScroll(true);
      setShowScrollButton(false);
    }
  };

  // ✅ Check if message indicates "in progress" activity
  const isInProgressMessage = (message: string) => {
    const inProgressKeywords = [
      'Searching',
      'Analyzing',
      'Evaluating',
      'Processing',
      'Analyzing findings',
      'Refining',
      'Deep analysis',
      'Structuring',
      'Finalizing'
    ];
    return inProgressKeywords.some(keyword => message.includes(keyword));
  };

  return (
    <Card
      className={cn(
        'border transition-all duration-300 ease-in-out',
        getStatusBgColor(),
        compact ? 'p-4' : 'p-6'
      )}
      style={{ direction: getLanguageDirection(i18n.language) }}
    >
      <CardContent className="p-0 flex flex-col max-h-full overflow-hidden transition-all duration-300 ease-in-out">
        {/* ✅ Elegant Header */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            {getStageIcon(progress.currentStage, getStageStatus(getCurrentStageIndex()))}
            <span className="text-sm font-semibold text-foreground">
              {t('legalAnalysis')}
            </span>
            <Badge 
              variant={progress.status === 'error' ? 'destructive' : progress.status === 'completed' ? 'default' : 'default'}
              className={cn(
                progress.status === 'completed' && 'bg-success text-success-foreground',
                progress.status === 'active' && 'bg-gold text-primary-foreground'
              )}
            >
              {getCurrentStageIndex() + 1} of {STAGE_DEFINITIONS.length}
            </Badge>
          </div>
          
          {/* ✅ Rich Status Text */}
          <p className={cn(
            'text-sm font-medium mb-2',
            progress.status === 'error' ? 'text-destructive' : 'text-foreground'
          )}>
            {progress.stageDetails}
          </p>
          
          {/* ✅ Polling indicator */}
          {isPollingMode && progress.status === 'active' && (
            <p className="text-xs text-muted-foreground italic opacity-80 mt-1">
              {t('waitingForResponse')}
            </p>
          )}
          
          {/* ✅ Sub-query details if in sub-query processing */}
          {progress.currentStage === 'sub_query_processing' && progress.stageDetails.includes('/') && (
            <p className="text-xs text-muted-foreground block">
              {progress.stageDetails}
            </p>
          )}
        </div>

        {/* ✅ Progress Bar */}
        <div className="mb-2">
          {isPollingMode ? (
            <Progress className="h-1.5 animate-pulse" />
          ) : (
            <Progress value={progress.overallProgress} className="h-1.5" />
          )}
        </div>

        {/* ✅ Progress Info with Expand Button */}
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs text-muted-foreground">
            {isPollingMode ? 'Checking status...' : `${progress.overallProgress}% complete`}
          </span>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setExpanded(!expanded)}
            className="h-6 w-6 p-0"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {/* ✅ Expandable Detailed Stages */}
        {expanded && (
          <div className="mt-2">
            <span className="text-xs text-muted-foreground font-medium block mb-2">
              {t('analysisSteps')}
            </span>
            
            {STAGE_DEFINITIONS.map((stage, index) => {
              const stageStatus = getStageStatus(index);
              return (
                <div 
                  key={stage.id}
                  className={cn(
                    'flex items-center gap-2 py-1.5 px-2 rounded-md transition-smooth',
                    stageStatus === 'active' && 'bg-gold/8'
                  )}
                >
                  {getStageIcon(stage.id, stageStatus)}
                  <div className="flex-1">
                    <p className={cn(
                      'text-xs',
                      stageStatus === 'active' && 'font-semibold',
                      stageStatus === 'error' && 'text-destructive',
                      stageStatus === 'completed' && 'text-success',
                      stageStatus === 'active' && 'text-gold',
                      stageStatus === 'pending' && 'text-muted-foreground'
                    )}>
                      {stage.name}
                    </p>
                    <p className="text-[10px] text-muted-foreground italic">
                      {stage.description}
                    </p>
                    {stageStatus === 'active' && stage.id === progress.currentStage && (
                      <p className="text-[10px] text-gold font-medium mt-1">
                        {progress.stageDetails}
                      </p>
                    )}
                  </div>
                  {stageStatus === 'completed' && (
                    <CheckCircle className="w-3.5 h-3.5 text-success" />
                  )}
                  {stageStatus === 'error' && (
                    <AlertCircle className="w-3.5 h-3.5 text-destructive" />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ✅ Activity Feed Section - Fixed height container */}
        {displayedFeed && displayedFeed.length > 0 && (
          <div className="mt-2 relative flex-shrink-0 transition-all duration-300 ease-in-out">
            <span className="text-[10px] text-muted-foreground font-medium block mb-1">
              Activity Feed
            </span>
            
            {/* Feed container with fade-out effect - FIXED 50px HEIGHT */}
            <div className="relative h-[50px] transition-all duration-300 ease-in-out">
              {/* Fade-out gradient overlay at top - matches Card background */}
              <div 
                className={cn(
                  "absolute top-0 left-0 right-0 h-4 pointer-events-none z-10 bg-gradient-to-b",
                  getFeedFadeColor()
                )}
              />
              
              {/* Scrollable feed container - takes full height of parent */}
              <div
                ref={feedContainerRef}
                onScroll={handleFeedScroll}
                className="h-full overflow-y-auto scrollbar-hide space-y-0.5 py-1"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                {displayedFeed.map((feedItem, index) => {
                  const isLast = index === displayedFeed.length - 1;
                  const shouldPulse = isLast && isInProgressMessage(feedItem.message);
                  return (
                    <div
                      key={feedItem.id}
                      className={cn(
                        'text-[9px] leading-tight text-muted-foreground px-1.5 py-0.5 rounded',
                        'transition-all duration-300',
                        isLast && 'animate-in slide-in-from-bottom-2',
                        shouldPulse && 'feed-pulse'
                      )}
                    >
                      <span className="opacity-50 mr-1">
                        {new Date(feedItem.timestamp).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </span>
                      <span>{feedItem.message}</span>
                    </div>
                  );
                })}
              </div>
              
              {/* Scroll-to-bottom button - smaller for compact feed */}
              {showScrollButton && (
                <div className="absolute bottom-0.5 right-0.5 z-20">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={handleScrollToBottom}
                    className="h-4 w-4 p-0 rounded-full bg-gold/10 hover:bg-gold/20 shadow-sm"
                  >
                    <ChevronDown className="h-2.5 w-2.5 text-gold" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SimpleLegalQueryProgress;
